/**
 * A signed-in player's name lives on their account (Auth displayName + profile doc) and on
 * every club they belong to (members.<uid>.name, players[], history). These helpers find
 * "me" by uid, so the two never have to be matched by name (#43).
 */

const nameOf = (p) => (typeof p === 'string' ? p : p?.name);

/** The name this uid has in a club, or null if they aren't a member. */
export function getMemberName(club, uid) {
  if (!club || !uid) return null;
  const name = club.members?.[uid]?.name;
  return typeof name === 'string' && name.trim() ? name : null;
}

/** The current user's name in the current club, falling back to their account name. */
export function resolveSelfName(club, uid, displayName) {
  return getMemberName(club, uid) || displayName || '';
}

/**
 * The name every club agrees this uid has, or null if they belong to no club or the
 * clubs disagree. Used to heal an account name left behind by an older club-only rename.
 */
export function findAgreedClubName(clubs, uid) {
  const names = new Set(
    (clubs || []).map((c) => getMemberName(c, uid)).filter(Boolean)
  );
  return names.size === 1 ? [...names][0] : null;
}

/**
 * Whether newName already belongs to someone other than this uid in a club, so renaming
 * to it would merge two different people's stats.
 */
export function isNameTakenByOther(club, uid, newName) {
  if (!club || !newName) return false;
  const target = newName.toLowerCase().trim();
  const myName = (getMemberName(club, uid) || '').toLowerCase().trim();
  if (target === myName) return false;
  const inPlayers = (club.players || []).some((p) => (nameOf(p) || '').toLowerCase().trim() === target);
  const inMembers = Object.entries(club.members || {}).some(
    ([memberUid, m]) => memberUid !== uid && (m?.name || '').toLowerCase().trim() === target
  );
  return inPlayers || inMembers;
}
